/**
 * One conversation.
 *
 * The header, the history and the composer. The details column belongs to the
 * shell, so what goes in it is rendered through a portal into the host element
 * the shell provides — that keeps the layout in one place while letting the
 * open room decide what is worth saying about itself.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactElement } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import { useJoinChannel, useMembers, useRoom } from '@/api/queries'
import { refreshCachedMessage, usePresence } from '@/api/store'
import type { Message, Room, User } from '@/api/types'
import { Avatar, Button, EmptyState, IconButton, Modal, Spinner, Tooltip, useToast } from '@/components/ui'
import { Composer } from '@/components/Composer'
import { DetailsPanel, roomAbilities } from '@/components/DetailsPanel'
import { MemberList } from '@/components/MemberList'
import { MessageList } from '@/components/MessageList'
import { ThreadPanel } from '@/components/ThreadPanel'
import { TypingIndicator } from '@/components/TypingIndicator'
import { RoomContent } from '@/components/RoomContent'
import { CallBar } from '@/components/CallBar'
import { useCalls, useRoomCall } from '@/components/CallProvider'
import { useAppLayout } from '@/routes/AppShell'
import styles from './RoomView.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

function personName(user: User): string {
  const display = user.display_name.trim()
  return display === '' ? user.username : display
}

function sharingSummary(room: Room): string | null {
  if (!room.is_shared && room.is_local) return null
  if (!room.is_local) return `Shared from ${room.owner_server_id}`

  const partners = room.shared_with ?? []
  if (partners.length === 0) return 'Shared with another server'
  return `Shared with ${partners.join(', ')}`
}

function MenuIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path d="M2.5 4h11M2.5 8h11M2.5 12h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function RoomSearchIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <circle cx="7.2" cy="7.2" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="m10.4 10.4 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function MembersIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <circle cx="6.2" cy="5.7" r="2.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.2 12.6c.5-2 2-3 4-3s3.5 1 4 3M10.6 4.1a2 2 0 0 1 0 3.6M12 12.6c-.1-1.1-.5-2-1.2-2.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

export function RoomView(): ReactElement {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const threadId = searchParams.get('thread')
  const { user, server } = useAuth()
  const { room, ready } = useRoom(roomId)
  const { data: members } = useMembers(room?.id)
  const presence = usePresence()
  const layout = useAppLayout()
  const [showMembers, setShowMembers] = useState(false)
  const [editingMessage, setEditingMessage] = useState<Message | null>(null)
  const [view, setView] = useState<'messages' | 'links' | 'widgets'>('messages')
  useEffect(() => setView('messages'), [roomId, threadId])
  const activeEdit = editingMessage?.room_id === roomId ? editingMessage : null
  useEffect(() => setEditingMessage(null), [roomId])
  const joinChannel = useJoinChannel()
  const toast = useToast()
  const { activeCall, callsEnabled, openRoomCall, joinExistingCall } = useCalls()
  const roomCall = useRoomCall(roomId)

  const label = useMemo(() => {
    if (room === undefined) return ''
    if (room.kind === 'channel') return room.name ?? room.slug ?? 'Channel'

    const everyone = members ?? []
    const others = everyone.filter((member) => member.user.id !== user?.id)
    const chosen = others.length > 0 ? others : everyone
    if (chosen.length === 0) return room.name ?? 'Direct message'
    return chosen.map((member) => personName(member.user)).join(', ')
  }, [room, members, user])

  /**
   * The other person in a one-to-one conversation, when there is exactly one.
   *
   * Read from the membership the header already has, so putting a face on the
   * room costs no extra request. A group DM has no single counterpart and keeps
   * the plain title.
   */
  const counterpart = useMemo((): User | null => {
    if (room === undefined || room.kind === 'channel') return null

    const others = (members ?? []).filter((member) => member.user.id !== user?.id)
    return others.length === 1 ? (others[0]?.user ?? null) : null
  }, [members, room, user])

  const openThread = useCallback(
    (rootId: string): void => {
      if (searchParams.get('thread') === rootId) return
      const next = new URLSearchParams(searchParams)
      next.set('thread', rootId)
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const closeThread = useCallback((): void => {
    const next = new URLSearchParams(searchParams)
    next.delete('thread')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const joinCurrent = useCallback(async (): Promise<void> => {
    if (room === undefined) return

    try {
      await joinChannel.mutateAsync(room.id)
      toast.success(`You joined #${room.name ?? room.slug ?? 'channel'}.`)
    } catch (error) {
      const message =
        isApiError(error) ? error.message : error instanceof Error ? error.message : 'The channel could not be joined.'
      toast.error(message)
    }
  }, [joinChannel, room, toast])

  if (!ready) {
    return (
      <div className={styles.centered}>
        <Spinner size="lg" label="Loading" />
      </div>
    )
  }

  if (room === undefined) {
    return (
      <div className={styles.centered}>
        <EmptyState
          title="This conversation is not available"
          description="It may have been deleted, or you may no longer be a member."
        />
      </div>
    )
  }

  const sharing = sharingSummary(room)
  const archived = room.archived_at !== null
  // Every public channel on this server is listed to every account, joined or
  // not, and posting needs a membership the reader may not have. Offering the
  // composer anyway would take a message the server is bound to refuse.
  const joined = room.my_membership != null

  const counterpartIsBot = counterpart?.is_bot === true
  const cannotPost = archived
    ? 'This room is archived. Nothing more can be posted in it.'
    : counterpartIsBot
      ? `${personName(counterpart)} is an automated bot. Direct messages to bots are read-only.`
      : joined
        ? null
        : room.is_local && room.visibility === 'public'
          ? 'Join this channel to post messages and receive notifications.'
          : 'You are viewing this channel without membership. Ask a Space admin to add you.'

  return (
    <div className={cx(styles.room, threadId !== null && styles.withThread)}>
      <header className={styles.header}>
        {layout.compact ? (
          <IconButton label="Show navigation" size="sm" onClick={layout.openSidebar}>
            <MenuIcon />
          </IconButton>
        ) : null}

        <div className={styles.identity}>
          <h1 className={styles.title}>
            {room.kind === 'channel' ? (
              <span className={styles.sigil} aria-hidden="true">
                #
              </span>
            ) : null}
            {counterpart !== null ? (
              <Avatar
                className={styles.titleAvatar}
                name={personName(counterpart)}
                id={counterpart.id}
                src={counterpart.avatar_url}
                size="sm"
                presence={
                  presence.get(counterpart.id)?.status === 'online' ? 'online' : 'offline'
                }
                decorative
              />
            ) : null}
            <span className={styles.titleText}>{label}</span>
            {counterpartIsBot ? (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  background: 'rgba(29, 96, 251, 0.1)',
                  color: 'var(--accent-primary)',
                  marginLeft: '4px',
                }}
              >
                BOT
              </span>
            ) : null}
            {sharing !== null ? (
              <Tooltip label={sharing}>
                <span className={styles.shared} role="img" aria-label={sharing}>
                  ↔
                </span>
              </Tooltip>
            ) : null}
          </h1>
        </div>

        <div className={styles.actions}>
          {/* One button: whether it is a video meeting is decided in the
              dialog, looking at your own camera, not from the toolbar. While
              a meeting is running the bar below the header is the way in, so
              the header does not offer a second one. */}
          {callsEnabled && joined && room.is_local && roomCall === null ? (
            <Button
              size="sm"
              variant="ghost"
              aria-label="Start a meeting"
              onClick={() => openRoomCall(room.id, 'Video meeting')}
            >
              Meet
            </Button>
          ) : null}
          {!archived && !joined && room.is_local && room.visibility === 'public' ? (
            <Button
              variant="primary"
              size="sm"
              loading={joinChannel.isPending}
              onClick={() => void joinCurrent()}
            >
              Join channel
            </Button>
          ) : null}
          <button
            type="button"
            className={styles.iconAction}
            aria-label="Search in this room"
            onClick={() =>
              navigate(`/search?q=${encodeURIComponent(`in:#${room.slug ?? room.name ?? room.id} `)}`)
            }
          >
            <RoomSearchIcon />
          </button>
          <button
            type="button"
            className={styles.membersButton}
            aria-label={`${room.member_count} members`}
            aria-pressed={showMembers}
            onClick={() => setShowMembers(true)}
          >
            <MembersIcon />
            <span className={styles.memberNumber}>{room.member_count}</span>
            <span className={styles.memberWord}>members</span>
          </button>
          <button type="button" className={styles.infoButton} aria-pressed={layout.detailsOpen} onClick={layout.toggleDetails}>
            <span className={styles.infoIcon} aria-hidden="true">ⓘ</span>
            Room details
          </button>
        </div>
      </header>

      {/* Not a notification: the room's own state, for as long as it lasts. */}
      {roomCall !== null ? (
        <CallBar
          call={roomCall}
          joined={activeCall?.id === roomCall.id}
          onJoin={() => joinExistingCall(roomCall, 'Join the video meeting')}
        />
      ) : null}

      {server?.capabilities?.room_content && room.is_local && room.kind === 'channel' ? <nav className={styles.contentTabs} aria-label="Channel sections">
        {(['messages', 'links', 'widgets'] as const).map((item) => <button key={item} type="button" aria-pressed={view === item} onClick={() => setView(item)}>
          {item === 'messages' ? 'Messages' : item === 'links' ? 'Links' : 'Widgets'}
        </button>)}
      </nav> : null}

      <Modal open={showMembers} onClose={() => setShowMembers(false)} title="Members" size="sm">
        <MemberList room={room} canManage={roomAbilities(room, user).canManageMembers} canAssignOwner={roomAbilities(room, user).canAssignOwner} />
      </Modal>

      {!room.is_local ? (
        <p className={styles.remote}>
          {`This room lives on ${room.owner_server_id}. Messages are delivered by that server.`}
        </p>
      ) : null}

      {view !== 'messages' ? <RoomContent key={`${room.id}-${view}`} room={room} view={view} /> : <div className={styles.workspace}>
        <div className={styles.conversation}>
          <MessageList room={room} roomName={label} onOpenThread={openThread} onEditMessage={setEditingMessage} />

          <div className={styles.composerDock}>
            <TypingIndicator roomId={room.id} className={styles.typing} />

            {/* Keyed so a draft never follows the reader into the next room. */}
            <Composer
              key={room.id}
              roomId={room.id}
              roomName={room.kind === 'channel' ? `#${label}` : label}
              autoFocus={threadId === null}
              disabled={cannotPost !== null}
              disabledReason={cannotPost ?? undefined}
              edit={activeEdit === null ? undefined : {
                id: activeEdit.id,
                text: activeEdit.text,
                onSave: async (text) => {
                  const updated = await api.patch<Message>(`/messages/${encodeURIComponent(activeEdit.id)}`, { text })
                  refreshCachedMessage(updated)
                  setEditingMessage((current) => current === activeEdit ? null : current)
                },
                onCancel: () => setEditingMessage(null),
              }}
            />
          </div>
        </div>

        {threadId !== null ? (
          <aside className={styles.thread} aria-label="Thread">
            <ThreadPanel
              key={`${room.id}-${threadId}`}
              room={room}
              rootId={threadId}
              roomName={room.kind === 'channel' ? `#${label}` : label}
              currentUsername={user?.qualified_id ?? null}
              disabled={cannotPost !== null}
              disabledReason={cannotPost ?? undefined}
              onClose={closeThread}
            />
          </aside>
        ) : null}
      </div>}

      {layout.detailsOpen && layout.detailsHost !== null
        ? createPortal(
            <DetailsPanel
              room={room}
              // A pane lying over the conversation is closed, not hidden.
              compact={layout.detailsOverlay}
              onClose={() => layout.setDetailsOpen(false)}
            />,
            layout.detailsHost,
          )
        : null}
    </div>
  )
}

export default RoomView
